import { can } from "@qmenut/permissions";
import { createFileRoute } from "@tanstack/react-router";

import * as api from "~/app/dashboard/api";
import { DashboardPage } from "~/app/dashboard/pages/dashboard-page";
import { dashboardSearchSchema } from "~/app/dashboard/types";
import { getSelectedBranch, hasSelectedFeature } from "~/shared/api";
import { getVisitsRange } from "~/shared/services/visit-series";

export const Route = createFileRoute("/_auth/")({
  validateSearch: dashboardSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { queryClient, roleCode, trpc } = context;
    const jobs: Array<Promise<unknown>> = [];
    if (can(roleCode, "analytics.read") && (await hasSelectedFeature(context, "analytics"))) {
      const range = getVisitsRange(deps.period);
      jobs.push(
        queryClient.query({ ...api.getAnalyticsSnapshotQueryOptions({ period: "15d", trpc }), staleTime: "static" }),
        queryClient.query({ ...api.getLoyaltySummaryQueryOptions({ trpc }), staleTime: "static" }),
        queryClient.query({ ...api.getLoyaltyVisitsQueryOptions({ ...range, trpc }), staleTime: "static" }),
      );
    }
    const branch = await getSelectedBranch(context);
    const tenant = await queryClient.query({ ...trpc.admin.tenant.me.queryOptions(), staleTime: "static" });
    if (tenant.restaurant.sourceCurrency === "USD" && can(roleCode, "exchangeRates.write")) {
      jobs.push(queryClient.query({ ...api.getExchangeRatesSummaryQueryOptions({ trpc }), staleTime: "static" }));
    }
    if (branch && can(roleCode, "loyalty.operate") && (await hasSelectedFeature(context, "loyalty"))) {
      jobs.push(
        queryClient.query({ ...api.getPendingRedemptionsQueryOptions({ trpc }), staleTime: "static" }),
        queryClient.query({ ...api.getVenueCodeQueryOptions({ branchId: branch.id, trpc }), staleTime: "static" }),
      );
    }
    if (branch) {
      jobs.push(
        queryClient.query({ ...api.getMenuCategoriesQueryOptions({ branchId: branch.id, trpc }), staleTime: "static" }),
        queryClient.query({ ...api.getMenuDishesQueryOptions({ branchId: branch.id, trpc }), staleTime: "static" }),
      );
    }
    // Non-blocking: the page renders its static frame and each card suspends on its own query.
    void Promise.allSettled(jobs);
  },
  component: DashboardPage,
});
